import {useState, useEffect} from "react";

import { withTranslation } from "react-i18next";
import {Box, InputAdornment, TextField, Typography} from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import Snackbar from "@plannel/components/Snackbar"
import Dialog from "@plannel/components/Dialog";
import {isEmpty, isEqual} from "lodash";
import planConfigService from "@plannel/services/data/plan-config-service";

const RatioRangeSettings = (props) => {
  const { t, open, setOpen, planConfig, setPlanConfig, title, configCd = "ITEM_HRCHY_RATIO_RANGE" } = props;

  const modalTitle = isEmpty(title) ? t("ratioRange") : title;
  const { snackBarMessageColor } = ColorUtil;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [max, setMax] = useState(planConfig?.objVal?.max ?? 100);
  const [min, setMin] = useState(planConfig?.objVal?.min ?? 0);

  const MIN = "min";
  const MAX = "max";

  const handleSave = () => {
    if (min > max) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `${t("MSG_ValueGreater")} ${min}` });
      return;
    }

    const configObj = planConfig?.objVal ?? {} ;

    configObj[MIN] = min;
    configObj[MAX] = max;

    const saveData = { ...planConfig, moduleCd: CodeUtil.module.DP, configCd, activeFlg: true, objVal: configObj };
    planConfigService.upsert([saveData])
      .then(response => {
        if (response.data) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          setOpen(false);
          const planConfigData = response.data;
          setPlanConfig(planConfigData[0]);
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
  };

  const initialSetting = () => {
    if (planConfig?.objVal) {
      setMin(planConfig.objVal?.min);
      setMax(planConfig.objVal?.max);
    } else {
      setMin(0);
      setMax(100);
    }
  }

  const handleClose = (action) => {
    if (isEqual(action, "cancel")) {
      setOpen(false);
      initialSetting()
    } else if (isEqual(action, "save")) {
      handleSave();
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      handleSave();
    }
  };

  const checkValidation = numVal => {
    return isNaN(numVal) || isEqual(numVal, "") || !Number.isInteger(Number(numVal));
  }

  const inputPropsByValue = value => {
    return value ? { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>%</InputAdornment> } : {};
  }

  useEffect(() => {
    if (planConfig?.objVal) {
      setMin(planConfig.objVal?.min);
      setMax(planConfig.objVal?.max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planConfig]);

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={() => handleClose("cancel")}
        title={modalTitle}
        onSave={() => handleClose("save")}
        width={332}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              size="small"
              name="min"
              key={"min"}
              InputProps={inputPropsByValue(min)}
              label={t(MIN)}
              value={min}
              sx={{ maxWidth: 100 }}
              required
              error={checkValidation(min)}
              onChange={e => setMin(parseInt(e.target.value, 10))}
            />
            <Typography sx={{ ml: 2, mr: 2 }} variant="h6" component="span">-</Typography>
            <TextField
              size="small"
              name="max"
              key={"max"}
              InputProps={inputPropsByValue(max)}
              label={t(MAX)}
              value={max}
              sx={{ maxWidth: 100 }}
              required
              error={checkValidation(max)}
              onChange={e => setMax(parseInt(e.target.value, 10))}
            />
          </Box>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  )
};

export default withTranslation()(RatioRangeSettings);